import { applyAction, type ActionResult, type GameAction } from '../game/actions';
import type { GameEvent } from '../game/events';
import { tick } from '../game/simulation';
import type { GameState } from '../game/state';
import { Emitter } from '../utils/Emitter';

/**
 * - `solo`: a local farm, saved to this browser.
 * - `host`: the local farm, opened to visitors. We stay the authority.
 * - `client`: visiting someone else's farm; their host is the authority.
 */
export type SessionMode = 'solo' | 'host' | 'client';

/** Actor id used for events caused by the simulation itself. */
export const WORLD_ACTOR = 'world';

export interface ActorEvent {
  /** Player id that caused the event, or WORLD_ACTOR. */
  actor: string;
  event: GameEvent;
}

/**
 * Owns the authoritative-or-mirrored GameState and routes actions. In solo and
 * host mode actions are applied locally; in client mode they are forwarded to
 * the host through `outbox` and the resulting state arrives via `replaceState`.
 */
export class GameController {
  readonly events = new Emitter<ActorEvent[]>();
  /** Fired when state changes because of an action or snapshot (not every tick). */
  readonly changed = new Emitter<void>();
  /** Fired with a human-readable reason when a local action is rejected. */
  readonly rejected = new Emitter<string>();

  private _mode: SessionMode = 'solo';
  private outbox: ((action: GameAction) => void) | null = null;

  constructor(
    private _state: GameState,
    public localPlayerId: string,
  ) {}

  get state(): GameState {
    return this._state;
  }

  get mode(): SessionMode {
    return this._mode;
  }

  get isAuthority(): boolean {
    return this._mode !== 'client';
  }

  setMode(mode: SessionMode, outbox: ((action: GameAction) => void) | null = null): void {
    this._mode = mode;
    this.outbox = mode === 'client' ? outbox : null;
  }

  /** Requests an action on behalf of the local player. */
  dispatch(action: GameAction): void {
    if (this._mode === 'client') {
      this.outbox?.(action);
      return;
    }
    this.applyAuthoritative(action, this.localPlayerId);
  }

  /** Applies an action as the authority (used for local and remote players). */
  applyAuthoritative(action: GameAction, actor: string): ActionResult {
    const result = applyAction(this._state, action);
    if (result.ok) {
      this.publish(result.events.map((event) => ({ actor, event })));
      this.changed.emit();
    } else if (actor === this.localPlayerId) {
      this.rejected.emit(result.reason);
    }
    return result;
  }

  update(dt: number): void {
    const events = tick(this._state, dt, { authoritative: this.isAuthority });
    if (events.length > 0) {
      this.publish(events.map((event) => ({ actor: WORLD_ACTOR, event })));
      this.changed.emit();
    }
  }

  /** Swaps in a whole new state (loading a save, host snapshot, leaving a session). */
  replaceState(state: GameState): void {
    this._state = state;
    this.changed.emit();
  }

  /** Re-emits events that happened elsewhere (e.g. on the host) for local FX. */
  publish(events: ActorEvent[]): void {
    if (events.length > 0) this.events.emit(events);
  }
}
